import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/context/AuthContext'
import { updateProfile, changePassword, getAuthProviders, linkGoogleAccount } from '@/api/auth'
import { SectionHead } from '@/components/aurora'
import { Camera, Pencil, Eye, EyeOff, Check } from 'lucide-react'
import GoogleSignInButton from '@/components/GoogleSignInButton'
import { startTelegramOidcLink } from '@/utils/telegramOidc'

const OIDC_CLIENT_ID = import.meta.env.VITE_TELEGRAM_OIDC_CLIENT_ID || ''

function Card({ children }) {
  return (
    <div className="rounded-2xl border px-5 py-5" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
      {children}
    </div>
  )
}

function SectionLabel({ children }) {
  return (
    <h3 className="text-[12.5px] font-semibold uppercase tracking-wider m-0 mb-3" style={{ color: 'var(--ink-3)' }}>
      {children}
    </h3>
  )
}


function InputField({ label, value, onChange, type = 'text', placeholder, right }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-[12px] font-medium" style={{ color: 'var(--ink-3)' }}>{label}</label>}
      <div className="relative flex items-center">
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full rounded-xl px-3 py-2.5 text-[13.5px] border outline-none transition-all"
          style={{
            background: 'var(--bg-soft)',
            borderColor: 'var(--border)',
            color: 'var(--ink)',
            paddingRight: right ? '2.5rem' : undefined,
          }}
        />
        {right && <div className="absolute right-3">{right}</div>}
      </div>
    </div>
  )
}

function SaveBtn({ loading, disabled, children }) {
  return (
    <button
      type="submit"
      disabled={disabled || loading}
      className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-all"
      style={{
        background: disabled || loading ? 'var(--border)' : 'var(--coral)',
        color: disabled || loading ? 'var(--ink-3)' : '#fff',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
      }}
    >
      {loading ? '...' : children}
    </button>
  )
}

function StatusMsg({ type, msg }) {
  if (!msg) return null
  return (
    <p className="text-[12.5px] m-0" style={{ color: type === 'ok' ? 'var(--mint)' : 'var(--danger)' }}>
      {msg}
    </p>
  )
}

function initials(user) {
  const name = user?.full_name || user?.username || '?'
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
}

export default function SettingsPage() {
  const { t }                 = useTranslation()
  const { user, refreshUser } = useAuth()

  // ── Connected accounts ────────────────────────────────────────────
  const [providers, setProviders] = useState(null)
  const [linkMsg, setLinkMsg] = useState(() => {
    const err = sessionStorage.getItem('tg_link_error')
    if (err) sessionStorage.removeItem('tg_link_error')
    return err ? { type: 'err', text: err } : null
  })
  const [googleLinking, setGoogleLinking] = useState(false)

  useEffect(() => {
    getAuthProviders().then(({ data }) => setProviders(data)).catch(() => {})
  }, [])

  function handleTelegramLink() {
    startTelegramOidcLink(OIDC_CLIENT_ID, `${window.location.origin}/`, '/settings')
  }

  function handleGoogleLinkCredential(idToken) {
    setGoogleLinking(true); setLinkMsg(null)
    linkGoogleAccount(idToken)
      .then(() => { refreshUser(); setLinkMsg({ type: 'ok', text: t('settings.saved', 'Saqlandi') }) })
      .catch((err) => setLinkMsg({ type: 'err', text: err.response?.data?.detail || t('common.error', 'Xatolik yuz berdi') }))
      .finally(() => setGoogleLinking(false))
  }

  // ── Avatar ─────────────────────────────────────────────────────────
  const fileRef    = useRef(null)
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [avatarLoading, setAvatarLoading] = useState(false)
  const [avatarMsg,     setAvatarMsg]     = useState(null)

  const avatarUrl = avatarPreview || user?.avatar || null

  function handleAvatarPick(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setAvatarPreview(URL.createObjectURL(file))
    const fd = new FormData()
    fd.append('avatar', file)
    setAvatarLoading(true)
    setAvatarMsg(null)
    updateProfile(fd)
      .then(() => { refreshUser(); setAvatarMsg({ type: 'ok', text: t('settings.saved', 'Saqlandi') }) })
      .catch(() => setAvatarMsg({ type: 'err', text: t('common.error', 'Xatolik yuz berdi') }))
      .finally(() => setAvatarLoading(false))
  }

  // ── Profile (name + email) ─────────────────────────────────────────
  const [fullName,     setFullName]     = useState(user?.full_name || '')
  const [email,        setEmail]        = useState(user?.email || '')
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileMsg,     setProfileMsg]     = useState(null)

  function handleProfileSave(e) {
    e.preventDefault()
    setProfileLoading(true)
    setProfileMsg(null)
    updateProfile({ full_name: fullName, email })
      .then(() => { refreshUser(); setProfileMsg({ type: 'ok', text: t('settings.saved', 'Saqlandi') }) })
      .catch(err => {
        const detail = err?.response?.data
        const msg = typeof detail === 'string' ? detail : Object.values(detail || {}).flat().join(' ')
        setProfileMsg({ type: 'err', text: msg || t('common.error', 'Xatolik yuz berdi') })
      })
      .finally(() => setProfileLoading(false))
  }

  // ── Password ───────────────────────────────────────────────────────
  const [newPw,  setNewPw]  = useState('')
  const [showNew, setShowNew] = useState(false)
  const [pwLoading, setPwLoading] = useState(false)
  const [pwMsg,     setPwMsg]     = useState(null)

  function handlePasswordSave(e) {
    e.preventDefault()
    setPwLoading(true)
    setPwMsg(null)
    changePassword(newPw)
      .then(() => {
        setNewPw('')
        setPwMsg({ type: 'ok', text: t('settings.passwordChanged', 'Parol muvaffaqiyatli o\'zgartirildi') })
      })
      .catch(err => {
        const detail = err?.response?.data
        const msg = typeof detail === 'string' ? detail : Object.values(detail || {}).flat().join(' ')
        setPwMsg({ type: 'err', text: msg || t('common.error', 'Xatolik yuz berdi') })
      })
      .finally(() => setPwLoading(false))
  }

  const eyeStyle = { color: 'var(--ink-3)', cursor: 'pointer', flexShrink: 0 }

  return (
    <div className="flex flex-col gap-4">
      <SectionHead title={t('sidebar.settings', 'Sozlamalar')}/>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Avatar + Name card */}
      <Card>
        <SectionLabel>{t('settings.profile', 'Profil')}</SectionLabel>

        {/* Avatar */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={avatarLoading}
            className="relative flex-shrink-0 w-16 h-16 rounded-full overflow-hidden flex items-center justify-center text-lg font-bold transition-opacity hover:opacity-80"
            style={{ background: 'var(--coral)', color: '#fff' }}
            title={t('settings.changeAvatar', 'Rasmni o\'zgartirish')}
          >
            {avatarUrl
              ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
              : initials(user)
            }
            <span
              className="absolute inset-0 flex items-center justify-center rounded-full opacity-0 hover:opacity-100 transition-opacity"
              style={{ background: 'rgba(0,0,0,0.35)' }}
            >
              <Camera size={18} color="#fff" />
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarPick} />

          <div className="flex flex-col gap-0.5">
            <span className="text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>
              {user?.full_name || user?.username}
            </span>
            <span className="text-[12.5px]" style={{ color: 'var(--ink-3)' }}>@{user?.username}</span>
            <button
              onClick={() => fileRef.current?.click()}
              className="text-[12px] flex items-center gap-1 mt-0.5"
              style={{ color: 'var(--coral)' }}
            >
              <Pencil size={11} strokeWidth={2} />
              {t('settings.changeAvatar', "Rasmni o'zgartirish")}
            </button>
          </div>
        </div>
        <StatusMsg type={avatarMsg?.type} msg={avatarMsg?.text} />

        {/* Name + email form */}
        <form onSubmit={handleProfileSave} className="flex flex-col gap-3 mt-3">
          <InputField
            label={t('settings.fullName', 'To\'liq ism')}
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            placeholder={t('auth.fullNamePlaceholder', 'Familiya Ism')}
          />
          <InputField
            label={t('settings.email', 'Email')}
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="email@example.com"
            type="email"
          />
          <div className="flex items-center justify-between gap-3">
            <StatusMsg type={profileMsg?.type} msg={profileMsg?.text} />
            <SaveBtn loading={profileLoading} disabled={!fullName.trim()}>
              {t('common.save', 'Saqlash')}
            </SaveBtn>
          </div>
        </form>
      </Card>

      {/* Connected accounts */}
      <Card>
        <SectionLabel>{t('settings.connectedAccounts', 'Bog\'langan hisoblar')}</SectionLabel>
        <div className="flex flex-col gap-3">
            {providers?.telegram?.enabled && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-[13.5px] font-medium" style={{ color: 'var(--ink)' }}>Telegram</span>
                {user?.telegram_connected ? (
                  <span className="flex items-center gap-1 text-[12.5px] font-semibold" style={{ color: 'var(--mint)' }}>
                    <Check size={14} /> {t('settings.connected', 'Bog\'langan')}
                  </span>
                ) : (
                  <button
                    onClick={handleTelegramLink}
                    className="px-3 py-1.5 rounded-lg text-[12.5px] font-semibold"
                    style={{ background: '#2ba5e0', color: '#fff' }}
                  >
                    {t('settings.connect', 'Bog\'lash')}
                  </button>
                )}
              </div>
            )}
            {providers?.google?.enabled && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-[13.5px] font-medium" style={{ color: 'var(--ink)' }}>Google</span>
                {user?.google_connected ? (
                  <span className="flex items-center gap-1 text-[12.5px] font-semibold" style={{ color: 'var(--mint)' }}>
                    <Check size={14} /> {t('settings.connected', 'Bog\'langan')}
                  </span>
                ) : googleLinking ? (
                  <span className="text-[12.5px]" style={{ color: 'var(--ink-3)' }}>{t('common.loading', 'Yuklanmoqda…')}</span>
                ) : (
                  <GoogleSignInButton
                    clientId={providers.google.client_id}
                    onCredential={handleGoogleLinkCredential}
                    onError={() => setLinkMsg({ type: 'err', text: t('auth.googleLoadFailed', "Google login'ni yuklab bo'lmadi") })}
                  />
                )}
              </div>
            )}
            {/* Sign in with Apple — backend only supports Apple as a login
                identity (AppleAuthView), not linking to an already-authenticated
                account, so this mirrors the mobile app's "coming soon" placeholder
                until a real link endpoint exists. */}
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13.5px] font-medium" style={{ color: 'var(--ink)' }}>Apple</span>
              {user?.apple_connected ? (
                <span className="flex items-center gap-1 text-[12.5px] font-semibold" style={{ color: 'var(--mint)' }}>
                  <Check size={14} /> {t('settings.connected', 'Bog\'langan')}
                </span>
              ) : (
                <button
                  disabled
                  className="px-3 py-1.5 rounded-lg text-[12.5px] font-semibold"
                  style={{ background: 'var(--border)', color: 'var(--ink-3)', cursor: 'not-allowed' }}
                >
                  {t('settings.comingSoon', 'Tez kunda')}
                </button>
              )}
            </div>
        </div>
        <div className="mt-2"><StatusMsg type={linkMsg?.type} msg={linkMsg?.text} /></div>
      </Card>

      {/* Password */}
      {!user?.tg_id && (
        <Card>
          <SectionLabel>{t('settings.changePassword', 'Parolni o\'zgartirish')}</SectionLabel>
          <form onSubmit={handlePasswordSave} className="flex flex-col gap-3">
            <InputField
              label={t('settings.newPassword', 'Yangi parol')}
              value={newPw}
              onChange={e => setNewPw(e.target.value)}
              type={showNew ? 'text' : 'password'}
              placeholder="••••••••"
              right={
                <button type="button" onClick={() => setShowNew(v => !v)} style={eyeStyle}>
                  {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              }
            />
            <div className="flex items-center justify-between gap-3">
              <StatusMsg type={pwMsg?.type} msg={pwMsg?.text} />
              <SaveBtn loading={pwLoading} disabled={!newPw}>
                {t('common.save', 'Saqlash')}
              </SaveBtn>
            </div>
          </form>
        </Card>
      )}
      </div>

    </div>
  )
}
